import React, { Component, ErrorInfo } from "react";

import { IFormControlDefinition, IRuntimeControlProps } from "./types";
import Loading from "../Loading";
import { Form, Alert } from "reactstrap";
import DynamicControl from "./DynamicControl";
import { IControlProps, IValidationMessage } from "./types";
import { CustomControl } from "./CustomControl";
import { ApiError } from "../../api/request/ApiError";
import { IDispatchRaiseException, raiseException } from "../../redux/notifications/actions";
import { connect } from "react-redux";
import UserError from "../UserError";

type Validation<T> = {
    readonly [P in keyof T]: IValidationMessage;
}
export type Definition<T> = {
    readonly [P in keyof T]: IFormControlDefinition;
}
export interface IRenderProps<T> {
    isSaving: boolean;
    save: () => Promise<boolean>;
    DynamicControl: React.FunctionComponent<{ field: IFormControlDefinition<any> } & Partial<IRuntimeControlProps>>,
    CustomControl: React.FunctionComponent<{ field: IFormControlDefinition<any>, render: React.ComponentType<IControlProps<any, any>> }>,
    ValidationMessages: React.FunctionComponent<{ showSavedSuccess: boolean }>,
    value: T;
}
export interface IFormProps<TData, TProps> {
    context?: any;
    passthrough?: TProps;
    busyWhenSaving?: boolean;
    load?: () => TData | Promise<TData>;
    submit?: (data: TData) => Promise<TData | boolean | void> | void;
    validationError?: (messages: string[]) => void;
    disableOnSave?: boolean;

    onChange?: (data: TData) => void;

    saveHook?: (save: () => Promise<boolean>) => void;
}
export interface IConfig<TData, TConfig> {
    initial: TData,
    definition: TConfig,
    noForm?: boolean;
    showSaveSuccess?: boolean,
    validationMessages?: 'hide' | 'compact',
    beforeChange?: (updated: TData, previous: TData, context?: any) => TData,
    customValidation?: (data: TData, context?: any) => Promise<Partial<Validation<TData>>>,
    rootValidator?: (data: TData) => IValidationMessage,
}

export function createFormDefinition<TData, TConfig extends Definition<TData>, TRender extends IRenderProps<TData> & TProps, TProps = {}>(config: IConfig<TData, TConfig>, render: React.ComponentType<TRender & TProps>, propsType?: TProps) {
    return { config, render, propsType };
}
function withForm<TData, TConfig extends Definition<TData>, TProps = {}>(config: IConfig<TData, TConfig>, Render: React.ComponentType<IRenderProps<TData> & TProps>, propsType?: TProps): React.ComponentType<IFormProps<TData, TProps>> {
    interface IReduxDispatch
        extends IDispatchRaiseException { }
    interface IProps
        extends IFormProps<TData, TProps>, IReduxDispatch { }

    interface IState {
        error: Error | string;
        isDirty: boolean;
        isSaved: boolean;
        loading: boolean;
        saving: boolean;
        renewValidation: number;
        value: TData;
        validation: Partial<Validation<TData>>;
        validationMessages: string;
    }

    interface IDynamicProps extends IRuntimeControlProps {
        field: IFormControlDefinition;
    }
    interface ICustomProps extends IRuntimeControlProps {
        field: IFormControlDefinition;
        render: React.ComponentType<IControlProps<any, any>>;
    }

    const labelMap: { [field: string]: string } = Object.keys(config.definition).reduce<{ [field: string]: string }>((p, k) => ({ ...p, [k]: config.definition[k] && config.definition[k].config.label }), {});

    function validationMessages(validationResult: any): string[] {
        return Object.keys(validationResult)
            .map((x) => validationResult[x])
            .filter((x) => x)
            .map((x) => x.message);
    }

    class FormController extends Component<IProps, IState> {
        constructor(props) {
            super(props);

            this.state = {
                error: null,
                isDirty: false,
                isSaved: false,
                loading: true,
                saving: false,
                renewValidation: 0,
                value: config.initial,
                validation: {},
                validationMessages: null,
            };
        }

        private isFormMounted = false;

        private readonly MappedDynamic: React.FunctionComponent<IDynamicProps> =
            ({ field, readOnly }) =>
                this.state.value && <DynamicControl
                    context={this.props.context}
                    readOnly={readOnly}
                    value={this.state.value[field.key]}
                    renewValidation={this.state.renewValidation}
                    definition={field}
                    validation={this.state.validation[field.key]}
                    onChange={(value) => this.onChange(value, field)}
                />;
        private readonly MappedCustom: React.FunctionComponent<ICustomProps> =
            ({ field, render, readOnly }) =>
                this.state.value && <CustomControl
                    context={this.props.context}
                    readOnly={readOnly}
                    value={this.state.value[field.key]}
                    renewValidation={this.state.renewValidation}
                    definition={field}
                    validation={this.state.validation[field.key]}
                    onChange={(value) => this.onChange(value, field)}
                    render={render} />;

        public componentDidMount() {
            this.isFormMounted = true;
            this.open();
            if (this.props.saveHook)
                this.props.saveHook(() => this.save());
        }
        public componentWillUnmount() {
            this.isFormMounted = false;
        }
        public componentDidCatch(e: Error, info: ErrorInfo) {
            console.error(e);
            console.info(info.componentStack);

            if (this.isFormMounted) {
                this.setState({
                    error: ApiError.raiseOrUser(e, this.props.raiseException, 'Er is een onverwachte fout opgetreden')
                });
            }
        }

        private async open() {
            try {
                if (!this.state.loading)
                    this.setState({ loading: true, error: null });

                let data: TData;
                if (this.props.load) {
                    const load = this.props.load();
                    if (load && typeof (load as Promise<TData>).then === 'function')
                        data = await load;
                    else
                        data = load as TData;

                    if (!this.isFormMounted)
                        return;
                }
                if (config.beforeChange)
                    data = config.beforeChange(data, null);
                this.setState({
                    value: data || config.initial,
                    loading: false,
                    renewValidation: 0,
                    error: null,
                });
            }
            catch (e) {
                if (!this.isFormMounted)
                    return;

                this.setState({ loading: false, error: ApiError.raiseOrUser(e, this.props.raiseException, 'Er is een fout opgetreden bij het laden van de gegevens.') });
            }
        }
        private onChange(value: any, fieldConfig: IFormControlDefinition) {
            if (typeof (value) === 'undefined' || value === undefined)
                value = null;

            const key = fieldConfig.key;
            const current = this.state.value || {} as any;

            if (current[key] !== value) {
                let update = {
                    ...current,
                    [key]: value
                };
                if (typeof config.beforeChange === 'function')
                    update = config.beforeChange(update, current, this.props.context);

                const validation = fieldConfig.validateSelf(value, update);
                let validationUpdate = this.state.validation;
                if (
                    (((validation && !this.state.validation[key]) || !validation)
                        && (!validation && this.state.validation[key]))
                    || (validation && validation.message !== this.state.validation[key])
                ) {
                    validationUpdate = { ...validationUpdate, [key]: validation };
                }
                this.setState({
                    isDirty: true,
                    value: update,
                    validation: validationUpdate,
                    validationMessages: validationMessages(validationUpdate).join('\n')
                });

                if (this.props.onChange)
                    this.props.onChange(update);
            }
        }

        private async save() {
            try {
                let validationResult = validate(this.state.value);
                if (config.customValidation) {
                    const custom = await config.customValidation(this.state.value);
                    if (Object.keys(custom).length > 0) {
                        validationResult = {
                            ...custom,
                            ...validationResult
                        };
                    }
                }

                if (Object.keys(validationResult).length > 0) {
                    const messages = validationMessages(validationResult);

                    this.setState({
                        validation: validationResult as any,
                        renewValidation: this.state.renewValidation + 1,
                        validationMessages: messages.join('\n'),
                    });

                    if (this.props.validationError)
                        this.props.validationError(messages);

                    return false;
                }
                this.setState({ saving: true, error: null });

                const result = await (this.props.submit(this.state.value) || Promise.resolve(false));
                if (typeof (result) === 'boolean') {
                    if (result && this.props.load) {
                        const load = this.props.load();
                        let data: TData;
                        if (load && typeof (load as Promise<TData>).then === 'function')
                            data = await load;
                        else
                            data = load as TData;
                        this.setState({ saving: false, isDirty: false, isSaved: true, value: data, error: null, renewValidation: 0 });
                    }
                    else {
                        this.setState({ saving: false, isDirty: false, isSaved: true, error: null });
                    }
                }
                else if (result) {
                    this.setState({ saving: false, isDirty: false, isSaved: true, value: result, error: null });
                }
                else {
                    this.setState({ saving: false, isDirty: false, isSaved: true, error: null });
                }

                return true;
            }
            catch (e) {
                this.setState({ saving: false, error: ApiError.raiseOrUser(e, this.props.raiseException, 'Er is een fout opgetreden bij het opslaan van de gegevens.') });
                return false;
            }
        }

        private readonly ValidationMessages: React.FunctionComponent<{ showSavedSuccess?: boolean }> = ({ showSavedSuccess }) => {

            return <>
                {!!this.state.renewValidation && !!this.state.validationMessages &&
                    <Alert color="warning" title={this.state.validationMessages}>
                        Het formulier bevat fouten
                </Alert>}
                {showSavedSuccess && this.state.isSaved && !this.state.isDirty &&
                    <Alert color="success">Wijzigingen opgeslagen</Alert>}
            </>;
        }

        private formBody() {
            return <>
                {this.state.error && <UserError error={this.state.error} fieldLabelMap={labelMap} />}

                {(config.validationMessages || 'compact') === 'compact' && <this.ValidationMessages showSavedSuccess={config.showSaveSuccess} />}

                {!this.state.loading && (
                    <Render
                        {...this.props.passthrough}
                        isSaving={this.state.saving}
                        save={() => this.save()}
                        DynamicControl={this.MappedDynamic}
                        CustomControl={this.MappedCustom}
                        ValidationMessages={this.ValidationMessages}
                        value={this.state.value}
                    />
                )}
            </>;

        }
        public render() {
            return <React.Fragment>
                {(this.state.loading || (this.props.busyWhenSaving && this.state.saving)) && <Loading />}

                {config.noForm
                    ? this.formBody()
                    : <Form formNoValidate noValidate disabled={this.props.disableOnSave && this.state.saving} onSubmit={(evt) => evt.preventDefault()}>
                        {this.formBody()}
                    </Form>}
            </React.Fragment>;
        }
    }

    function validate(data: TData) {
        const result: Partial<Validation<TData>> = {};

        for (const key of Object.keys(config.definition)) {
            const control: IFormControlDefinition<any> = config.definition[key];

            if (control && control.validateFromRoot) {
                const value = control.validateFromRoot(data);
                if (value)
                    result[key] = value;
            }
        }
        if (config.rootValidator) {
            const value = config.rootValidator(data);
            if (value)
                result[''] = value;
        }

        return result;
    }

    return connect<{}, IReduxDispatch>(null, { raiseException })(FormController);
}
export default withForm;


export function withFormUncontrolled<TData, TConfig extends Definition<TData>, TProps = {}>(config: IConfig<TData, TConfig>, props: IFormProps<TData, TProps>, Render: React.ComponentType<IRenderProps<TData>>): React.FunctionComponent {
    const Control = withForm(config, Render);

    return () => <Control {...props as any} />;
}